import { useEffect, useRef } from 'react'

import {
  combineScheduleStreams,
  type CombineScheduleStreamsArgs,
  type CombineScheduleStreamsResult,
} from '../combineScheduleStreams'

/**
 * Enough admitted rows that the document scrolls — which is what re-arms
 * scroll-driven pagination. Below this the list is stuck: nothing to scroll
 * means nothing to trigger the next page. Mirrors the games list's own
 * `MIN_DISPLAYABLE_ROWS`.
 */
export const MIN_ADMITTED_ROWS = 12

/** Hard stop, so a pathological dataset cannot spin forever. Mirrors the games
 *  list's `RAW_PAGE_AUTO_FOLLOW_CAP`. */
export const STALL_FOLLOW_CAP = 8

interface TimedGame {
  starts_at?: string | null
}

export interface UseScheduleStreamArgs<TGame extends TimedGame>
  extends CombineScheduleStreamsArgs<TGame> {
  fetchNextGames: () => void
  fetchNextEvents: () => void
  /** True while either stream has a request in flight. Gates the stall guard. */
  isFetching: boolean
  /** Changes when the filters or period change; resets the stall counter. */
  resetKey: string
}

export interface UseScheduleStreamResult<TGame extends TimedGame>
  extends CombineScheduleStreamsResult<TGame> {
  /** Advances whichever stream is holding the boundary back. */
  fetchNextPage: () => void
}

/**
 * The merged schedule list's paging: what to render now, and what to fetch on
 * scroll-end.
 *
 * Takes frontier DESCRIPTORS rather than query objects, so it does not care how
 * either stream is fetched. That matters in practice: the competition list's
 * games come from an infinite query while My Schedule's come from a context
 * that pages by hand, and both feed this unchanged.
 *
 * ## The stall guard
 *
 * The games hook runs an auto-follow of its own, but it counts PRE-watermark
 * rows. With a lagging event stream you can hold forty games and admit three —
 * that guard sees forty, decides the list is scrollable, and stops. Nothing is
 * then scrollable, so nothing triggers another page and the list sits frozen
 * short of its first screen. This one counts ADMITTED rows and can advance
 * either stream, which is the only combination that unsticks that case.
 */
export const useScheduleStream = <TGame extends TimedGame>({
  fetchNextGames,
  fetchNextEvents,
  isFetching,
  resetKey,
  ...combineArgs
}: UseScheduleStreamArgs<TGame>): UseScheduleStreamResult<TGame> => {
  'use memo'

  const combined = combineScheduleStreams(combineArgs)
  const { nextStream, hasNextPage, visibleGames, visibleEvents } = combined

  const fetchNextPage = () => {
    if (nextStream === 'games' || nextStream === 'both') {
      fetchNextGames()
    }
    if (nextStream === 'events' || nextStream === 'both') {
      fetchNextEvents()
    }
  }

  //  A ref, not state: the effect below must not re-run just because the count
  //  changed, or a slow API turns one follow into a render loop.
  const followedRef = useRef(0)
  const keyRef = useRef('')

  if (keyRef.current !== resetKey) {
    keyRef.current = resetKey
    followedRef.current = 0
  }

  const admittedCount = visibleGames.length + visibleEvents.length

  useEffect(() => {
    if (isFetching || !hasNextPage) {
      return
    }
    if (admittedCount >= MIN_ADMITTED_ROWS) {
      return
    }
    if (followedRef.current >= STALL_FOLLOW_CAP) {
      return
    }
    followedRef.current += 1
    fetchNextPage()
    // `fetchNextPage` is excluded on purpose: it is rebuilt every render, so
    // listing it would re-run this on every commit. The values it reads are
    // already in the deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetching, hasNextPage, admittedCount, resetKey])

  return { ...combined, fetchNextPage }
}
